import jsPDF from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';

/**
 * Export Session Data Utility
 * Handles exporting complete session data in various formats (PDF, Excel, JSON)
 */

/**
 * Export session data as PDF
 */
const exportSessionPDF = (sessionData) => {
  const doc = new jsPDF();
  const { session, activities, participants, responses } = sessionData;

  // Title
  doc.setFontSize(20);
  doc.setTextColor(37, 99, 235); // primary-600
  doc.text('Training Session Report', 14, 20);

  // Session Information
  doc.setFontSize(12);
  doc.setTextColor(0, 0, 0);
  doc.text('Session Information', 14, 35);

  doc.setFontSize(10);
  const sessionInfo = [
    ['Session Code', session.code || 'N/A'],
    ['Title', session.title || 'Untitled Session'],
    ['Status', session.status || 'Unknown'],
    ['Date', session.date ? new Date(session.date).toLocaleDateString('en-GB', { timeZone: 'UTC' }) : 'N/A'],
    ['Duration', session.duration ? `${session.duration} minutes` : 'N/A'],
    ['Total Participants', participants.length.toString()],
    ['Total Activities', activities.length.toString()],
  ];

  doc.autoTable({
    startY: 40,
    head: [],
    body: sessionInfo,
    theme: 'plain',
    styles: { fontSize: 9 },
    columnStyles: {
      0: { fontStyle: 'bold', cellWidth: 50 },
      1: { cellWidth: 130 },
    },
  });

  // Participants Section
  let currentY = doc.lastAutoTable.finalY + 15;

  if (currentY > 250) {
    doc.addPage();
    currentY = 20;
  }

  doc.setFontSize(12);
  doc.setTextColor(0, 0, 0);
  doc.text('Participants', 14, currentY);

  const participantData = participants.map((p, index) => [
    (index + 1).toString(),
    p.name || 'Anonymous',
    p.email || 'N/A',
    p.organization || 'N/A',
    p.joinedAt ? new Date(p.joinedAt).toLocaleString('en-GB') : 'N/A',
    p.online !== false ? 'Online' : 'Offline',
  ]);

  doc.autoTable({
    startY: currentY + 5,
    head: [['#', 'Name', 'Email', 'Organization', 'Joined At', 'Status']],
    body: participantData,
    theme: 'striped',
    styles: { fontSize: 8 },
    headStyles: { fillColor: [37, 99, 235] },
  });

  // Activities Section
  currentY = doc.lastAutoTable.finalY + 15;

  if (currentY > 250) {
    doc.addPage();
    currentY = 20;
  }

  doc.setFontSize(12);
  doc.text('Activities Summary', 14, currentY);

  activities.forEach((activity, index) => {
    currentY = doc.lastAutoTable.finalY ? doc.lastAutoTable.finalY + 10 : currentY + 10;

    if (currentY > 260) {
      doc.addPage();
      currentY = 20;
    }

    // Activity Header
    doc.setFontSize(11);
    doc.setTextColor(37, 99, 235);
    doc.text(
      `${index + 1}. ${activity.title || 'Untitled Activity'}`,
      14,
      currentY
    );

    // Activity Details
    doc.setFontSize(9);
    doc.setTextColor(100, 100, 100);
    doc.text(
      `Type: ${activity.type?.replace('_', ' ') || 'Unknown'} | Time Limit: ${
        activity.config?.timeLimit || 'N/A'
      } min`,
      14,
      currentY + 5
    );

    // Activity Responses
    const activityResponses = responses.filter(
      (r) => r.activityId === activity.id
    );

    if (activityResponses.length > 0) {
      const responseData = activityResponses.slice(0, 10).map((r) => [
        r.participantName || 'Anonymous',
        r.answer || JSON.stringify(r.response) || 'No response',
        r.score !== undefined ? r.score.toString() : 'N/A',
        r.submittedAt ? new Date(r.submittedAt).toLocaleTimeString('en-GB') : 'N/A',
      ]);

      doc.autoTable({
        startY: currentY + 8,
        head: [['Participant', 'Response', 'Score', 'Time']],
        body: responseData,
        theme: 'grid',
        styles: { fontSize: 7 },
        headStyles: { fillColor: [229, 231, 235] },
        margin: { left: 20 },
      });

      if (activityResponses.length > 10) {
        const remaining = activityResponses.length - 10;
        doc.setFontSize(8);
        doc.setTextColor(100, 100, 100);
        doc.text(
          `... and ${remaining} more response(s)`,
          20,
          doc.lastAutoTable.finalY + 3
        );
      }
    } else {
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      doc.text('No responses yet', 20, currentY + 8);
    }
  });

  // Footer
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);
    doc.text(
      `Page ${i} of ${pageCount}`,
      doc.internal.pageSize.width / 2,
      doc.internal.pageSize.height - 10,
      { align: 'center' }
    );
    doc.text(
      `Generated on ${new Date().toLocaleString()}`,
      14,
      doc.internal.pageSize.height - 10
    );
  }

  // Save PDF
  const fileName = `session-${session.code || 'report'}-${Date.now()}.pdf`;
  doc.save(fileName);

  return fileName;
};

/**
 * Export session data as Excel
 */
const exportSessionExcel = (sessionData) => {
  const { session, activities, participants, responses } = sessionData;

  const workbook = XLSX.utils.book_new();

  // Session Info Sheet
  const sessionInfoData = [
    ['Session Report'],
    [],
    ['Session Code', session.code || 'N/A'],
    ['Title', session.title || 'Untitled Session'],
    ['Status', session.status || 'Unknown'],
    ['Date', session.date ? new Date(session.date).toLocaleDateString('en-GB', { timeZone: 'UTC' }) : 'N/A'],
    ['Duration', session.duration ? `${session.duration} minutes` : 'N/A'],
    ['Total Participants', participants.length],
    ['Total Activities', activities.length],
    ['Total Responses', responses.length],
    [],
    ['Generated', new Date().toLocaleString()],
  ];

  const sessionInfoSheet = XLSX.utils.aoa_to_sheet(sessionInfoData);
  XLSX.utils.book_append_sheet(workbook, sessionInfoSheet, 'Session Info');

  // Participants Sheet
  const participantsData = [
    ['#', 'Name', 'Email', 'Organization', 'Joined At', 'Status'],
    ...participants.map((p, index) => [
      index + 1,
      p.name || 'Anonymous',
      p.email || 'N/A',
      p.organization || 'N/A',
      p.joinedAt ? new Date(p.joinedAt).toLocaleString('en-GB') : 'N/A',
      p.online !== false ? 'Online' : 'Offline',
    ]),
  ];

  const participantsSheet = XLSX.utils.aoa_to_sheet(participantsData);
  XLSX.utils.book_append_sheet(workbook, participantsSheet, 'Participants');

  // Activities Sheet
  const activitiesData = [
    [
      '#',
      'Title',
      'Type',
      'Status',
      'Time Limit',
      'Responses',
      'Avg Score',
      'Completion Rate',
    ],
    ...activities.map((activity, index) => {
      const activityResponses = responses.filter(
        (r) => r.activityId === activity.id
      );
      const avgScore =
        activityResponses.length > 0
          ? (
              activityResponses.reduce((sum, r) => sum + (r.score || 0), 0) /
              activityResponses.length
            ).toFixed(2)
          : 'N/A';
      const completionRate =
        participants.length > 0
          ? `${Math.round(
              (activityResponses.length / participants.length) * 100
            )}%`
          : 'N/A';

      return [
        index + 1,
        activity.title || 'Untitled',
        activity.type?.replace('_', ' ') || 'Unknown',
        activity.completed ? 'Completed' : 'Pending',
        activity.config?.timeLimit ? `${Math.round(activity.config.timeLimit / 60)} min` : 'N/A',
        activityResponses.length,
        avgScore,
        completionRate,
      ];
    }),
  ];

  const activitiesSheet = XLSX.utils.aoa_to_sheet(activitiesData);
  XLSX.utils.book_append_sheet(workbook, activitiesSheet, 'Activities');

  // All Responses Sheet
  const responsesData = [
    [
      'Activity',
      'Participant',
      'Response',
      'Score',
      'Max Score',
      'Submitted At',
    ],
    ...responses.map((r) => {
      const activity = activities.find((a) => a.id === r.activityId);
      return [
        activity?.title || 'Unknown Activity',
        r.participantName || 'Anonymous',
        r.answer || JSON.stringify(r.response) || 'No response',
        r.score !== undefined ? r.score : 'N/A',
        r.maxScore !== undefined ? r.maxScore : 'N/A',
        r.submittedAt ? new Date(r.submittedAt).toLocaleString('en-GB') : 'N/A',
      ];
    }),
  ];

  const responsesSheet = XLSX.utils.aoa_to_sheet(responsesData);
  XLSX.utils.book_append_sheet(workbook, responsesSheet, 'All Responses');

  // Individual Activity Sheets (up to 10 activities)
  activities.slice(0, 10).forEach((activity, index) => {
    const activityResponses = responses.filter(
      (r) => r.activityId === activity.id
    );

    if (activityResponses.length > 0) {
      const activityResponsesData = [
        ['Activity:', activity.title || 'Untitled'],
        ['Type:', activity.type?.replace('_', ' ') || 'Unknown'],
        ['Responses:', activityResponses.length],
        [],
        ['Participant', 'Response', 'Score', 'Time Taken', 'Submitted At'],
        ...activityResponses.map((r) => [
          r.participantName || 'Anonymous',
          r.answer || JSON.stringify(r.response) || 'No response',
          r.score !== undefined ? r.score : 'N/A',
          r.timeTaken ? `${r.timeTaken}s` : 'N/A',
          r.submittedAt ? new Date(r.submittedAt).toLocaleString('en-GB') : 'N/A',
        ]),
      ];

      const activitySheet = XLSX.utils.aoa_to_sheet(activityResponsesData);
      const sheetName = `Activity ${index + 1}`.slice(0, 31); // Excel sheet name limit
      XLSX.utils.book_append_sheet(workbook, activitySheet, sheetName);
    }
  });

  // Save Excel file
  const fileName = `session-${session.code || 'report'}-${Date.now()}.xlsx`;
  XLSX.writeFile(workbook, fileName);

  return fileName;
};

/**
 * Export session data as JSON
 */
const exportSessionJSON = (sessionData) => {
  const { session, activities, participants, responses } = sessionData;

  const jsonData = {
    meta: {
      exportedAt: new Date().toISOString(),
      version: '1.0',
    },
    session: {
      ...session,
      participantCount: participants.length,
      activityCount: activities.length,
      responseCount: responses.length,
    },
    participants,
    activities: activities.map((activity) => {
      const activityResponses = responses.filter(
        (r) => r.activityId === activity.id
      );
      return {
        ...activity,
        responseCount: activityResponses.length,
        completionRate:
          participants.length > 0
            ? Math.round((activityResponses.length / participants.length) * 100)
            : 0,
        responses: activityResponses,
      };
    }),
    summary: {
      totalParticipants: participants.length,
      totalActivities: activities.length,
      totalResponses: responses.length,
      completedActivities: activities.filter((a) => a.completed).length,
      averageParticipation:
        activities.length > 0
          ? Math.round(
              (responses.length / (participants.length * activities.length)) * 100
            )
          : 0,
    },
  };

  // Create blob and download
  const blob = new Blob([JSON.stringify(jsonData, null, 2)], {
    type: 'application/json',
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  const fileName = `session-${session.code || 'report'}-${Date.now()}.json`;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);

  return fileName;
};

/**
 * Main export function
 */
export const exportSessionData = (sessionData, format = 'pdf') => {
  if (!sessionData || !sessionData.session) {
    throw new Error('Invalid session data');
  }

  switch (format.toLowerCase()) {
    case 'pdf':
      return exportSessionPDF(sessionData);
    case 'excel':
    case 'xlsx':
      return exportSessionExcel(sessionData);
    case 'json':
      return exportSessionJSON(sessionData);
    default:
      throw new Error(`Unsupported export format: ${format}`);
  }
};

/**
 * Export individual participant data
 */
export const exportParticipantData = (participants, format = 'excel') => {
  if (format === 'excel') {
    const workbook = XLSX.utils.book_new();

    const participantsData = [
      ['#', 'Name', 'Email', 'Organization', 'Joined At', 'Status'],
      ...participants.map((p, index) => [
        index + 1,
        p.name || 'Anonymous',
        p.email || 'N/A',
        p.organization || 'N/A',
        p.joinedAt ? new Date(p.joinedAt).toLocaleString('en-GB') : 'N/A',
        p.online !== false ? 'Online' : 'Offline',
      ]),
    ];

    const sheet = XLSX.utils.aoa_to_sheet(participantsData);
    XLSX.utils.book_append_sheet(workbook, sheet, 'Participants');

    const fileName = `participants-${Date.now()}.xlsx`;
    XLSX.writeFile(workbook, fileName);
    return fileName;
  }

  // JSON export
  const blob = new Blob([JSON.stringify(participants, null, 2)], {
    type: 'application/json',
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  const fileName = `participants-${Date.now()}.json`;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);

  return fileName;
};

export default exportSessionData;
